import { useCallback, useEffect, useRef, useState } from 'react';
// import { getFlowsData } from '@/api/queries';
import { ThemeMinimalsProvider } from '@packages/ui-components';
import { trackFlowStart } from '../../utils/flowAnalytics';
import { FlowsMenu } from './components/FlowsMenu';
import { Flowbar } from './components/Flowbar';
import { FlowsContainerProps } from './types';
import { asyncStore } from '@/_helpers/base';
import { useSocket } from './hooks/useSocket';

const FlowsContainer = ({
  moveToSweep,
  hideElements,
  showElements,
  showCaseTemp,
  showcase,
  tags,
  setFlowsObjects,
  polygons,
  vsObjects,
  navigationManager,
  sweeps,
  allSweeps,
  currentFloor,
  setFlowActive,
  appState,
  flowsProps,
  mainFlow,
  flowsVisibleAssets,
  tourCustomButton,
  flowsMenuMeshComponent,
  flowsBarMeshComponent,
  vrEventManager,
  tour,
  startFlowFromAsset,
  setSelectedFlowAsset,
  socket,
  isLocked,
  isRemoteControlled,
  enableFlowRunOnLaunch,
  setMesurementsMode,
  parentMenusCount,
  isMenuOpen,
  setFlowsMenuOpen,
  isFlowsMenuOpen,
  menuWidth,
}: FlowsContainerProps) => {
  const [flows, setFlows] = useState([]);
  const [activeFlow, setActiveFlow] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [isMainFlowActive, setIsMainFlowActive] = useState(false);
  const assetsWithClick = useRef(null);

  const handleEmitActionBySocket = (actionName: string, data?: any) => {
    if (socket.connected && isRemoteControlled) {
      socket.emit(actionName, data);
    }
  };

  const handleAgree = useCallback(
    (flow, preventEmit = false) => {
      setSelectedFlowAsset(null);

      if (flow?.restart) {
        setFlows((prev) =>
          prev.map((f) => ({
            ...f,
            steps: f.id === flow.id ? f.steps?.map((s) => ({ ...s, completed: false })) : f.steps,
          })),
        );
      } else {
        setFlows((prev) =>
          prev.map((f) => ({ ...f, started: f.id === flow.id ? true : f.started })),
        );
      }

      setActiveFlow(flow);
      setIsDialogOpen(false);
      setSelectedFlow(null);
      setFlowActive(true);

      if (flow?.id) {
        trackFlowStart(flow.id);
      }

      if (!preventEmit && !flow?.isNextFlow) {
        handleEmitActionBySocket('agree_flow', flow);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleFlowSelect = (flow, preventEmit = false) => {
    console.log(
      'handleFlowSelect2',
      'flow',
      flow,
      'preventEmit',
      preventEmit,
      'asyncStore.vrIsOn',
      asyncStore.vrIsOn,
    );
    if (flow.started) {
      if (asyncStore.vrIsOn) {
        const event = new CustomEvent('vrFlowSelect', { detail: flow });
        document.dispatchEvent(event);
      }
      if (!asyncStore.vrIsOn) {
        setIsDialogOpen(true);
      }
      setSelectedFlow(flow);
      if (!preventEmit) {
        handleEmitActionBySocket('select_flow', flow);
      }
      return;
    }
    // if (!!flow.selectedFromMenu && !!asyncStore.vrIsOn && !!preventEmit) return;
    setSelectedFlowAsset(null);
    setActiveFlow({ ...flow, started: true });
    setFlowActive(true);
    setFlows((prev) => prev.map((f) => ({ ...f, started: f.id === flow.id ? true : f.started })));

    if (flow?.id) {
      trackFlowStart(flow.id);
    }

    if (!preventEmit) {
      handleEmitActionBySocket('select_flow', flow);
    }
  };

  const closeDialog = (_, preventEmit = false) => {
    setIsDialogOpen(false);
    setSelectedFlow(null);
    setSelectedFlowAsset(null);
    if (!preventEmit) {
      handleEmitActionBySocket('close_flow_dialog');
    }
  };

  useEffect(() => {
    if (flowsProps?.length && !flows?.length) {
      // You can await here
      setFlows(
        flowsProps?.map((f) => ({
          ...f,
          handleFlowSelect,
          started: false,
          steps: f.steps?.map((s) => ({ ...s, completed: false })),
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowsProps]);

  useEffect(() => {
    if (flows?.length) {
      const allAssetsWithClicks = new Map();
      flows.forEach((flow) => {
        const flowId = flow?.id;
        if (!flowId) return; // Skip if flowId is not available

        const flowAssets = [];

        flow?.steps?.forEach((step) => {
          if (step?.conditions) {
            flowAssets.push(
              ...step.conditions?.filter((c) => c.asset !== 'button' && c.action === 'click'),
            );
          }

          if (step?.triggers) {
            flowAssets.push(
              ...step.triggers?.filter(
                (t) => t.condition.asset !== 'button' && t.condition.action === 'click',
              ),
            );
          }
        });

        if (flowAssets.length > 0) {
          allAssetsWithClicks.set(flowId, flowAssets);
        }
      });
      assetsWithClick.current = allAssetsWithClicks;
    }
  }, [flows]);

  useEffect(() => {
    if (activeFlow && assetsWithClick.current) {
      const activeFlowAssets = assetsWithClick.current.get(activeFlow?.id);
      if (activeFlowAssets) {
        const assetsToDisableAction = activeFlowAssets.filter(
          (a) => a?.condition?.after === 'notActive' || a.after === 'notActive',
        );
        setFlowsObjects({ flowAssetsToDisableAction: assetsToDisableAction || [] });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlow]);

  useEffect(() => {
    if (
      !showCaseTemp ||
      !showcase ||
      !mainFlow ||
      appState !== 'playing' ||
      activeFlow ||
      isMainFlowActive
    )
      return;
    if (isRemoteControlled && !enableFlowRunOnLaunch) return;

    if (mainFlow) {
      setFlows((prev) =>
        prev.map((f) => ({ ...f, started: f.id === mainFlow.id ? true : f.started })),
      );
      setActiveFlow(mainFlow);
      setIsMainFlowActive(true);
      setFlowActive(true);

      if (mainFlow?.id) {
        trackFlowStart(mainFlow.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mainFlow,
    showCaseTemp,
    showcase,
    appState,
    activeFlow,
    isMainFlowActive,
    enableFlowRunOnLaunch,
  ]);

  const handleCloseFlowbar = useCallback(() => {
    return new Promise((resolve) => {
      setActiveFlow(null);
      setFlowActive(false);
      setFlowsObjects({ flowAssetsToDisableAction: [] });
      resolve(true); // Resolve the promise when done
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVRItemSelect = (e) => {
    handleFlowSelect(flows[e.target.dataset.indexNumber]);
  };

  useEffect(() => {
    if (flowsMenuMeshComponent) {
      flowsMenuMeshComponent.inputs.itemRefArr.forEach((menu) => {
        menu.addEventListener('click', handleVRItemSelect);
      });
      flowsMenuMeshComponent.triggerCallback = () => {
        if (flowsMenuMeshComponent.inputs.visible) {
          handleFlowSelect(flows[flowsMenuMeshComponent.selectedItemIndex]);
          flowsMenuMeshComponent.setVisible(false);
          setTimeout(() => {
            showCaseTemp.showcase.Sweep.enable(...sweeps.map((s) => s.sid));
          }, 500);
        }
      };
    }
    return () => {
      if (flowsMenuMeshComponent) {
        flowsMenuMeshComponent.inputs.itemRefArr.forEach((menu) => {
          menu.removeEventListener('click', handleVRItemSelect);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows, sweeps]);

  useEffect(() => {
    if (startFlowFromAsset && flows?.length) {
      const flow = flows.find((f) => f.id === startFlowFromAsset);
      if (flow && asyncStore?.vrIsOn) {
        handleFlowSelect(flow);
        return;
      }
      if (flow && !activeFlow) {
        handleFlowSelect(flow);
      } else {
        setIsDialogOpen(true);
        setSelectedFlow(flow);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFlowFromAsset, flows, activeFlow]);

  useSocket({ socket, isLocked, handleFlowSelect, handleAgree, closeDialog });

  useEffect(() => {
    if (activeFlow) {
      setMesurementsMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlow]);

  const language = tour?.client?.rtl ? 'he' : 'en';

  return flows?.length ? (
    <ThemeMinimalsProvider language={language}>
      {isRemoteControlled && !enableFlowRunOnLaunch ? (
        <></>
      ) : (
        <FlowsMenu
          flows={flows}
          tourCustomButton={tourCustomButton}
          parentMenusCount={parentMenusCount}
          isMenuOpen={isMenuOpen}
          menuWidth={menuWidth}
          setFlowsMenuOpen={(val: boolean) => setFlowsMenuOpen(val)}
          isFlowsMenuOpen={isFlowsMenuOpen}
        />
      )}
      <Flowbar
        flow={activeFlow}
        flows={flows}
        selectedFlow={selectedFlow}
        handleCloseFlowbar={handleCloseFlowbar}
        moveToSweep={moveToSweep}
        hideElements={hideElements}
        showElements={showElements}
        showCaseTemp={showCaseTemp}
        showcase={showcase}
        tags={tags}
        polygons={polygons}
        setFlows={setFlows}
        vsObjects={vsObjects}
        navigationManager={navigationManager}
        allSweeps={allSweeps}
        currentFloor={currentFloor}
        closeSwitchDialog={closeDialog}
        isSwitchDialogOpen={isDialogOpen}
        handleSwitchAgree={handleAgree}
        flowsVisibleAssets={flowsVisibleAssets}
        tour={tour}
        flowsBarMeshComponent={flowsBarMeshComponent}
        flowsMenuMeshComponent={flowsMenuMeshComponent}
        vrEventManager={vrEventManager}
        handleEmitActionBySocket={handleEmitActionBySocket}
        socket={socket}
        isLocked={isLocked}
        isRemoteControlled={isRemoteControlled}
      />
    </ThemeMinimalsProvider>
  ) : (
    <></>
  );
};

export default FlowsContainer;
